import * as React from 'react';
import { z } from 'zod';
import { FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { PhoneInput } from './PhoneInput';

export const accountDetailsSchema = z.object({
  fullName: z.string().trim().min(1, 'Enter your full name.'),
  company: z.string().trim().min(1, 'Enter your company name.'),
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
  // US format, as the field formats it while typing.
  phoneNumber: z
    .string()
    .trim()
    .min(1, 'Enter your phone number.')
    .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Enter a 10-digit phone number.'),
});

export type AccountDetailsValues = z.infer<typeof accountDetailsSchema>;
export type AccountDetailsField = keyof AccountDetailsValues;
export type AccountDetailsForm = ZodForm<typeof accountDetailsSchema>;

export interface AccountDetailsFieldsetProps {
  /** From `useZodForm(accountDetailsSchema, …)`. */
  form: AccountDetailsForm;
}

const FIELDS: ReadonlyArray<{
  name: AccountDetailsField;
  label: string;
  type: 'text' | 'email' | 'tel';
  placeholder: string;
}> = [
  {
    name: 'fullName',
    label: 'Full name',
    type: 'text',
    placeholder: 'Jane Smith',
  },
  { name: 'company', label: 'Company', type: 'text', placeholder: 'Acme Inc' },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'jane@acme.com',
  },
  {
    name: 'phoneNumber',
    label: 'Phone number',
    type: 'tel',
    placeholder: '(555) 555-0000',
  },
];

/**
 * The four fields that open a sign-up: name, company, email and phone. Enter
 * walks down the fields; on the last one it submits the surrounding form, as
 * Enter in a form does. The phone formats itself as it is typed.
 *
 *   <AccountDetailsFieldset form={form} />
 */
export function AccountDetailsFieldset({ form }: AccountDetailsFieldsetProps) {
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key !== 'Enter') return;
    const next = FIELDS[index + 1];
    if (!next) return;
    e.preventDefault();
    form.setFocus(next.name);
  };

  return (
    <>
      {FIELDS.map((field, index) => (
        <FormField
          key={field.name}
          form={form}
          name={field.name}
          label={field.label}
        >
          {(bound) =>
            field.name === 'phoneNumber' ? (
              <PhoneInput
                {...bound}
                placeholder={field.placeholder}
                onKeyDown={(e) => handleKeyDown(e, index)}
                size="lg"
              />
            ) : (
              <Input
                {...bound}
                type={field.type}
                placeholder={field.placeholder}
                onKeyDown={(e) => handleKeyDown(e, index)}
                size="lg"
              />
            )
          }
        </FormField>
      ))}
    </>
  );
}
